import { showCustomToast, updateCustomToast } from 'common/components/CustomToast/utils';
import { useMutation, useApolloClient } from '@apollo/client';
import { IDropdownOption } from '@fluentui/react';
import { invoiceDateFormat } from 'common/utils/dateFormats';
import { BlockBlobClient } from '@azure/storage-blob';
import React from 'react';

import { UploadDocumentFormModal } from './UploadDocumentFormModal';
import { EntityDocumentValues } from '../types';
import DOCUMENT_UPLOAD_STATUS from 'common/graphql/DocumentUploadStatusSubscription.graphql';
import UPLOAD_DOCUMENT from './UserUploadDocument.graphql';

import DOCUMENTSLIST from '../MyDocumentsList/PersonalDocuments.graphql';
import { getFileTypesList } from 'documents/documentAssignment/folder/list/FolderView/FolderViewHeader/UploadForm/utils';
import { ExtractionTypes_Query } from 'documents/documentAssignment/folder/list/FolderView/FolderViewHeader/UploadForm/__generated__/ExtractionTypes';


import { OnDocumentUploadStatus } from 'common/graphql/__generated__/DocumentUploadStatusSubscription';
import { DocumentTypeOptions } from 'common/types/utility';
import { PersoanlDocuments, PersoanlDocumentsVariables } from '../MyDocumentsList/__generated__/PersonalDocuments';
import { UserUploadDocument, UserUploadDocumentVariables } from './__generated__/UserUploadDocument';

interface UploadDocumentModalProps {
  visible: boolean;
  onDismiss: () => void;
  documentOptions?: DocumentTypeOptions[];
  companyCurrencies?: IDropdownOption[];
  defaultDocumentTypeId?: number;
  extractionTypesData?: ExtractionTypes_Query;
}

export const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({
  visible,
  onDismiss,
  documentOptions,
  companyCurrencies,
  defaultDocumentTypeId,
  extractionTypesData,
}) => {
  const client = useApolloClient();
   const [uploadDocument] = useMutation<
    UserUploadDocument,
    UserUploadDocumentVariables
  >(UPLOAD_DOCUMENT);

  const handleUpload = async (
    values: EntityDocumentValues,
    fileSelected: File[]
  ) => {
    const documentType = documentOptions?.find(
      (opt) => opt.key === values.entityDocumentTypeId
    );
    if (!documentType) return;

    const extractionTypeId = values.extractionTypeId;
    const content = values._documentContents;
    const indexName = values.indexName;
    const indexDescription = values.indexDescription;
    const indexReferenceNumber = values.indexReferenceNumber;
    const indexTransactionDate = values.indexTransactionDate;
    const indexAmount = values.indexAmount ? parseFloat(values.indexAmount) : null;
    const indexCurrencyId = values.indexCurrencyId;
    const comment = values.comment;

    let filteredFilesList: File[] = fileSelected;
    if (extractionTypeId) {
     
        const selectedExtractionType =
                  extractionTypesData?.extractionTypes?.nodes.find(
        (node) => node.id === extractionTypeId
      );
      const fileTypesAllowed = getFileTypesList(selectedExtractionType);

      filteredFilesList = fileSelected.filter((file) =>
        fileTypesAllowed?.includes(file.type)
      );
      if (fileSelected.length > filteredFilesList.length)
        showCustomToast({
          content: 'Files with invalid data types are not uploaded',
          appearance: 'error',
        });
    }

    filteredFilesList.map(async (fileEntity, fileIndex) => {
      const toastId = showCustomToast({
        content: `Uploading ${fileEntity?.name}...`,
        appearance: 'info',
        autoDismiss: false,
      });

      const uploadMutationResults = await uploadDocument({
        variables: {
          document: {
            documentTypeId: parseInt(documentType.key.toString()),
            description: content ? content : '',
            comment: comment,
            filename: fileEntity.name,
            indexName: indexName ? indexName : '',
            indexDescription: indexDescription ? indexDescription : '',
            indexReferenceNumber: indexReferenceNumber ? indexReferenceNumber : '',
            indexTransactionDate: indexTransactionDate
              ? invoiceDateFormat(indexTransactionDate)
              : null,
            indexAmount: indexAmount ? indexAmount : null,
            indexCurrencyId: indexCurrencyId ? indexCurrencyId : null,
            extractionTypeId: extractionTypeId,
          },
        },
      });

      if (uploadMutationResults.errors)
        updateCustomToast(toastId!, {
          content: `Upload of ${fileEntity.name} failed`,
          appearance: 'error',
          autoDismiss: true,
        });

      if (
        uploadMutationResults.data?.userUploadDocument?.document &&
        uploadMutationResults.data.userUploadDocument.document._documentFileId
      ) {
        const observer = client.subscribe({
          query: DOCUMENT_UPLOAD_STATUS,
          variables: {
            documentId:
              uploadMutationResults.data.userUploadDocument.document
                ._documentFileId!,
          },
        });

        const subscription = observer.subscribe((data) => {
          const subscribedData = data.data as OnDocumentUploadStatus;
          const { status, document } = { ...subscribedData.documentUploadStatus };

          if (status.type === 'VALIDATING') {
            updateCustomToast(toastId!, {
              content: status.message
                ? `Validating files ${fileEntity.name} - ${status.message}`
                : `Validating files ${fileEntity.name}`,
              appearance: 'info',
              autoDismiss: false,
            });
          } else if (status.type === 'EXTRACTING') {
            updateCustomToast(toastId!, {
              content: status.message
                ? `Extracting data from ${fileEntity.name} - ${status.message}`
                : `Extracting data from ${fileEntity.name}`,
              appearance: 'info',
              autoDismiss: false,
            });
          } else if (status.type === 'FAILURE') {
            subscription.unsubscribe();
            updateCustomToast(toastId!, {
              content: status.message
                ? `Upload of ${fileEntity.name} failed - ${status.message}`
                : `Upload of ${fileEntity.name} failed`,
              appearance: 'error',
              autoDismiss: true,
            });
          } else if (status.type === 'WARNING') {
            subscription.unsubscribe();
            updateCustomToast(toastId!, {
              content: status.message
                ? `Warning for file ${fileEntity.name}: ${status.message}`
                : `Warning for file ${fileEntity.name}`,
              appearance: 'warning',
              autoDismiss: true,
            });
          } else {
            subscription.unsubscribe();
            updateCustomToast(toastId!, {
              content: status.message
                ? `Successfully uploaded ${fileEntity.name}: ${status.message}`
                : `Successfully uploaded ${fileEntity.name}`,
              appearance: 'success',
              autoDismiss: true,
            });

            
             const cacheData = client.readQuery<
              PersoanlDocuments,
              PersoanlDocumentsVariables
            >({
              query: DOCUMENTSLIST,
            });

            client.writeQuery<PersoanlDocuments, PersoanlDocumentsVariables>({
              query: DOCUMENTSLIST,
              data: {
                entityDocumentPersonalPools: {
                  ...cacheData?.entityDocumentPersonalPools!,
                  nodes: [
                    { ...document! },
                    ...cacheData?.entityDocumentPersonalPools?.nodes!,
                  ],
                },
              },
            });
          }
        });

        const clientBlob = new BlockBlobClient(
          uploadMutationResults.data.userUploadDocument.uploadLink
        );
        await clientBlob.uploadData(fileEntity);
        updateCustomToast(toastId!, { content: `Validating ${fileEntity.name}...` });
      }
    });
  };

  return (
    <UploadDocumentFormModal
      visible={visible}
      onDismiss={onDismiss}
      title="Upload Documents"
      subtitle="Select a document to upload for this Folder"
      documentOptions={documentOptions}
      companyCurrencies={companyCurrencies}
      defaultDocumentTypeId={defaultDocumentTypeId}
      isEdit={false}
      onSubmit={handleUpload}
    />
  );
};